/**
 * Invite-accept landing page — `/invite/:token`. Backed by
 * `GET /api/invite/{token}` (preview, never burns the token — corporate
 * email scanners prefetch links, so a GET must be idempotent/safe) and
 * `POST /api/invite/{token}/accept` (burns it, mints a session).
 *
 * Public route — NOT wrapped by `RequireSession` (App.tsx unwraps it
 * before reaching the gate, same as `/login`): a fresh invitee has no
 * session yet by definition, and the token itself is the credential
 * (see `api/auth.py::accept_invite_route`'s own docstring for why
 * this endpoint is deliberately not CSRF-gated either — same
 * "the token in the URL IS the authority" reasoning applies here).
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Mail, ShieldCheck, XCircle } from 'lucide-react';

import { TuvionWordmark } from '@/components/brand/TuvionLogo';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { extractErrorMessage } from '@/lib/utils';
import api from '@/services/api';
import { useAuthSession } from '@/state/authSession';
import { InvitePreview } from '@/types';

type LoadState = 'loading' | 'ready' | 'not_found' | 'expired' | 'error';

interface Props {
  token: string;
}

export const InviteAcceptPage: React.FC<Props> = ({ token }) => {
  const navigate = useNavigate();
  const { refresh } = useAuthSession();

  const [state, setState] = useState<LoadState>('loading');
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getInvitePreview(token);
        if (cancelled) return;
        setPreview(data);
        setState('ready');
      } catch (e) {
        if (cancelled) return;
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 404) setState('not_found');
        else if (status === 410) setState('expired');
        else {
          setState('error');
          setErrorText(extractErrorMessage(e, 'Could not load this invite.'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    setErrorText(null);
    try {
      await api.acceptInvite(token);
      await refresh();
      // Deliberately a single landing spot regardless of invite shape
      // (superadmin vs event-organizer) — both roles land on the
      // fleet dashboard; an organizer sees their newly-granted event
      // in the event picker there. Keeps this page's redirect logic
      // simple and correct rather than guessing a "better" per-role
      // destination.
      navigate('/fleet', { replace: true });
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 410) {
        setState('expired');
      } else {
        setErrorText(extractErrorMessage(e, 'Could not accept this invite.'));
      }
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-base px-4">
      <Card className="w-full max-w-sm p-6">
        <div className="mb-5 flex items-center gap-2">
          <TuvionWordmark height={28} />
        </div>

        {state === 'loading' && (
          <p className="text-sm text-fg-secondary">Loading invite…</p>
        )}

        {state === 'not_found' && (
          <InviteMessage
            icon={<XCircle className="size-8 text-status-error" />}
            title="Invite not found"
            body="This link doesn't match any invite. Ask whoever invited you to send a fresh one."
          />
        )}

        {state === 'expired' && (
          <InviteMessage
            icon={<XCircle className="size-8 text-status-error" />}
            title="Invite expired"
            body="This invite has expired or was already used. Ask whoever invited you to send a fresh one."
          />
        )}

        {state === 'error' && (
          <InviteMessage
            icon={<XCircle className="size-8 text-status-error" />}
            title="Something went wrong"
            body={errorText ?? 'Could not load this invite.'}
          />
        )}

        {state === 'ready' && preview && (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              {preview.as_superadmin ? (
                <ShieldCheck className="mt-0.5 size-8 shrink-0 text-accent" />
              ) : (
                <CheckCircle2 className="mt-0.5 size-8 shrink-0 text-accent" />
              )}
              <div>
                <h1 className="text-lg font-semibold">
                  {preview.as_superadmin
                    ? 'You are invited as a TuviON admin'
                    : `You are invited to organize ${preview.event_name ?? 'an event'}`}
                </h1>
                <p className="mt-1 flex items-center gap-1 text-xs text-fg-secondary">
                  <Mail className="size-3" />
                  {preview.email}
                </p>
              </div>
            </div>

            <p className="text-xs text-fg-secondary">
              Expires {new Date(preview.expires_at).toLocaleString()}.
            </p>

            {errorText && <p className="text-sm text-status-error">{errorText}</p>}

            <Button onClick={() => void accept()} disabled={accepting} className="w-full">
              {accepting ? 'Accepting…' : 'Accept invite'}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

const InviteMessage: React.FC<{ icon: React.ReactNode; title: string; body: string }> = ({
  icon,
  title,
  body,
}) => (
  <div className="flex flex-col items-center gap-3 py-2 text-center">
    {icon}
    <h1 className="text-lg font-semibold">{title}</h1>
    <p className="text-sm text-fg-secondary">{body}</p>
  </div>
);

export default InviteAcceptPage;
